import React from 'react'
import { useParams } from 'react-router-dom'
import { CompletionStatus, SectionName } from '_gql'
import { yupResolver } from '@hookform/resolvers/yup'

import useNameFormatter from '@divvy-web/hooks.usenameformatter'
import { FormattedMessage } from '@divvy-web/i18n'
import Drawer, { DrawerActions, DrawerView } from '@divvy-web/skylab.drawer'
import { FormTextInput } from '@divvy-web/skylab.form'
import { useForm, useFormState } from '@divvy-web/skylab.react-hook-form'
import Scrim from '@divvy-web/skylab.scrim'
import { TOAST_TYPE_DANGER, TOAST_TYPE_SUCCESS, useToast } from '@divvy-web/skylab.toast'

import { limitInputLength } from '@sputnik/components/utils'
import { useGetBankAccountInfoLazyQuery } from '@sputnik/pages/CreditApplicationPages/ConnectBankAccountForm/GetBankAccountInfo.gql'
import { isBankInfoPopulated } from '@sputnik/pages/CreditApplicationPages/ConnectBankAccountForm/utils'
import { useApplicationProgress } from '@sputnik/pages/CreditApplicationPages/Navigation/useApplicationProgress'
import { hasMissingRequiredField } from '@sputnik/pages/CreditApplicationPages/ReviewForm/utils'
import { useWebTracking } from '@sputnik/tracking/WebTrackingContext'

import {
  AddManualBankAccountDrawerValidationSchema,
  addManualBankAccountDrawerValidationSchema,
} from './addManualBankAccountDrawerValidationSchema'
import { useAddManualBankAccountInfo } from './AddManualBankAccountInfo.gql'
import styles from './styles.module.scss'

export interface AddManualBankAccountDrawerProps {
  isShowing: boolean
  onClose: () => void
}

const AddManualBankAccountDrawer = ({ isShowing, onClose }: AddManualBankAccountDrawerProps) => {
  const [getClassName] = useNameFormatter('AddManualBankAccountDrawer')
  const { appId } = useParams()
  const showToast = useToast()
  const completionStatus = useApplicationProgress({ sectionName: SectionName.FINANCE_INFO })

  const [getBankAccountInfo] = useGetBankAccountInfoLazyQuery({
    variables: {
      creditApplicationId: appId ?? '',
    },
  })

  const [addManualBankAccountInfo] = useAddManualBankAccountInfo({
    onError: () => {
      showToast(
        TOAST_TYPE_DANGER,
        <FormattedMessage
          defaultMessage='Failed to update bank account information'
          id='sputnik.AddManualBankAccountDrawer__7jMB8q'
        />,
        { className: 'fs-unmask' },
      )
    },
    onCompleted: (updatedData) => {
      // tracking for Connect bank section and finance info section as a whole
      const financialInfoSectionComplete = updatedData.updateCreditApplication?.savedSectionsInfo?.find(
        (section) =>
          section?.sectionName === SectionName.FINANCE_INFO && section?.completionStatus === CompletionStatus.COMPLETED,
      )

      if (financialInfoSectionComplete && completionStatus !== CompletionStatus.COMPLETED) {
        tracker?.track('se_signup__application_financial_info_bank_connection_section_progress__completed', {
          is_complete: true,
        })
      }

      const { selectedBankInfo } = updatedData?.updateCreditApplication?.financeInfo ?? {}
      const { accountHolderName, accountNumberLastFour, bankName, routingNumber } = selectedBankInfo ?? {}

      const hasMissingInfo = hasMissingRequiredField([
        accountHolderName,
        accountNumberLastFour,
        bankName,
        routingNumber,
      ])

      if (!hasMissingInfo) {
        tracker?.track('se_signup__application_financial_info_details_section_progress__completed', {
          is_complete: true,
        })
      }
      showToast(
        TOAST_TYPE_SUCCESS,
        <FormattedMessage
          defaultMessage='Successfully added bank account'
          id='sputnik.AddManualBankAccountDrawer__vhuJ+Z'
        />,
      )
      onClose()
    },
  })

  const { handleSubmit, reset, control } = useForm<AddManualBankAccountDrawerValidationSchema>({
    mode: 'all',
    defaultValues: {
      bankName: '',
      routingNumber: '',
      confirmAccountNumber: '',
      accountNumber: '',
      confirmRoutingNumber: '',
    },
    resolver: yupResolver(addManualBankAccountDrawerValidationSchema),
  })

  const { isValid, isSubmitting } = useFormState({ control })

  const onCloseForm = () => {
    reset()
    onClose()
  }

  const { tracker } = useWebTracking()

  const onSubmit = async (formValues: AddManualBankAccountDrawerValidationSchema) => {
    tracker?.track('se_signup__application_financial_info_connect_manually_add_account__submitted')
    const { accountHolderName, bankName, accountNumber, routingNumber } = formValues

    const { data } = await getBankAccountInfo()
    const selectedBankAccount = data?.creditApplication?.financeInfo?.selectedBankInfo

    await addManualBankAccountInfo({
      variables: {
        creditApplicationId: appId ?? '',
        financeInfo: {
          // setting this to null makes the selectedBankInfo the manual bank info.
          // we only want to select this bank account ID when no bank info has been
          // selected prior (i.e. external bank account was linked first)
          ...(!isBankInfoPopulated(selectedBankAccount) && { selectedExternalId: null }),
          bankInfo: {
            accountHolderName,
            accountNumber,
            routingNumber,
            bankName,
          },
        },
      },
    })
  }

  return (
    <Scrim isShowing={isShowing}>
      <Drawer
        activeViewKey='default'
        className={`${styles[getClassName('')]} fs-unmask`}
        dataTestId='add-manual-bank-account'
        isShowing={isShowing}
        views={{
          default: (
            <DrawerView
              className='fs-unmask'
              footerActions={
                <DrawerActions
                  cancelText={
                    <span className='fs-unmask'>
                      <FormattedMessage
                        defaultMessage='Cancel'
                        id='sputnik.AddManualBankAccountDrawer__47FYwb'
                      />
                    </span>
                  }
                  className={styles[getClassName('actionButtons')]}
                  confirmButtonColor='action'
                  confirmText={
                    <span className='fs-unmask'>
                      <FormattedMessage
                        defaultMessage='Add bank account'
                        id='sputnik.AddManualBankAccountDrawer__YLqkWa'
                      />
                    </span>
                  }
                  form={getClassName('form')}
                  inactive={!isValid}
                  showSpinner={isSubmitting}
                  onCancel={onCloseForm}
                  onConfirm={handleSubmit(onSubmit)}
                />
              }
              title={
                <FormattedMessage
                  defaultMessage='Add a bank account'
                  id='sputnik.AddManualBankAccountDrawer__UgMsgI'
                />
              }
              onCloseClick={onCloseForm}
            >
              <form
                className={`${styles[getClassName('form')]} fs-unmask`}
                onSubmit={handleSubmit(onSubmit)}
              >
                <FormTextInput
                  className='fs-mask'
                  control={control}
                  label={
                    <span className='fs-unmask'>
                      <FormattedMessage
                        defaultMessage='Account holder name'
                        id='sputnik.AddManualBankAccountDrawer__kqidNP'
                      />
                    </span>
                  }
                  name='accountHolderName'
                />
                <FormTextInput
                  className='fs-mask'
                  control={control}
                  label={
                    <span className='fs-unmask'>
                      <FormattedMessage
                        defaultMessage='Bank name'
                        id='sputnik.AddManualBankAccountDrawer__QOLh4y'
                      />
                    </span>
                  }
                  name='bankName'
                />
                <FormTextInput
                  className='fs-mask'
                  control={control}
                  label={
                    <span className='fs-unmask'>
                      <FormattedMessage
                        defaultMessage='Routing number'
                        id='sputnik.AddManualBankAccountDrawer__Pk5sml'
                      />
                    </span>
                  }
                  name='routingNumber'
                  type='number'
                  onInput={(e) => limitInputLength(e, 9)}
                />
                <FormTextInput
                  className='fs-mask'
                  control={control}
                  label={
                    <span className='fs-unmask'>
                      <FormattedMessage
                        defaultMessage='Re-enter routing number'
                        id='sputnik.AddManualBankAccountDrawer__oRbFUj'
                      />
                    </span>
                  }
                  name='confirmRoutingNumber'
                  type='number'
                  onInput={(e) => limitInputLength(e, 9)}
                />
                <FormTextInput
                  className='fs-mask'
                  control={control}
                  label={
                    <span className='fs-unmask'>
                      <FormattedMessage
                        defaultMessage='Account number'
                        id='sputnik.AddManualBankAccountDrawer__/BgyNa'
                      />
                    </span>
                  }
                  name='accountNumber'
                  type='number'
                />
                <FormTextInput
                  className='fs-mask'
                  control={control}
                  label={
                    <span className='fs-unmask'>
                      <FormattedMessage
                        defaultMessage='Re-enter account number'
                        id='sputnik.AddManualBankAccountDrawer__2vMGi1'
                      />
                    </span>
                  }
                  name='confirmAccountNumber'
                  type='number'
                />
              </form>
            </DrawerView>
          ),
        }}
      />
    </Scrim>
  )
}

export default AddManualBankAccountDrawer
